import { UserFeedback, DetectionResult, GroundTruthLabel } from "../types";
import { sanitizeUserInput } from "./sanitize";
import { safeLocalStorage } from "./safeStorage";
import { countWords } from "./validation";
import { computeZScoresForText, computeFeatureZScores } from "./features";

const FEEDBACK_STORAGE_KEY = "catchai_model_feedback_v1";
const LEGACY_FEEDBACK_STORAGE_KEY = "veritext_model_feedback_v1";

function triggerDetectionCacheClear(): void {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("catchai_clear_analysis_cache"));
      fetch("/api/detect/clear-cache", { method: "POST" }).catch(() => {});
    }
  } catch {}
}

/**
 * Normalize text for reliable matching across editor, snippet, and history
 */
function normalizeTextForMatch(raw: string): string {
  return (raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ");
}

/**
 * Resolve whether a feedback record represents verified "human" or "ai" ground truth
 */
export function resolveFeedbackGroundTruth(fb: UserFeedback): "human" | "ai" | null {
  if (fb.actualLabel === "human" || fb.actualLabel === "hybrid_human_edited") {
    return "human";
  }
  if (fb.actualLabel === "ai" || fb.actualLabel === "hybrid_ai_assisted") {
    return "ai";
  }
  if (fb.isCorrect === false) {
    // User flagged the model verdict as wrong: invert or default borderline to human
    if (fb.prediction === "Likely Human-Written") return "ai";
    return "human";
  }
  if (fb.isCorrect === true) {
    if (fb.prediction === "Likely AI-Generated") return "ai";
    if (fb.prediction === "Likely Human-Written") return "human";
  }
  return null;
}

/**
 * Retrieve all saved user feedback entries from safe storage
 */
export function getStoredFeedback(): UserFeedback[] {
  try {
    const raw = safeLocalStorage.getItem(FEEDBACK_STORAGE_KEY) || safeLocalStorage.getItem(LEGACY_FEEDBACK_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch (err) {
    console.error("Failed to load user feedback:", err);
    return [];
  }
}

/**
 * Retrieve all user-flagged or verified samples from both feedback storage and scan history
 * so no flagged correction is ever lost.
 */
export function getAllFlaggedAndVerifiedSamples(): UserFeedback[] {
  const fromStore = getStoredFeedback();
  const seenTexts = new Set<string>(
    fromStore.map((f) => (f.fullText || f.textSnippet || "").trim().toLowerCase().slice(0, 120))
  );
  const combined: UserFeedback[] = [...fromStore];

  try {
    const histRaw =
      safeLocalStorage.getItem("catchai_history_v2") ||
      safeLocalStorage.getItem("veritext_history_v2");
    if (histRaw) {
      const parsed = JSON.parse(histRaw);
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (item && item.feedback && item.text) {
            const fb: UserFeedback = {
              ...item.feedback,
              fullText: item.feedback.fullText || item.text,
              textSnippet: item.feedback.textSnippet || item.text.slice(0, 120).trim(),
              prediction: item.feedback.prediction || item.result?.overall_prediction || "Uncertain",
              confidenceScore: item.feedback.confidenceScore ?? item.result?.confidence_score ?? 50
            };
            const key = (fb.fullText || fb.textSnippet || "").trim().toLowerCase().slice(0, 120);
            if (key && !seenTexts.has(key)) {
              seenTexts.add(key);
              combined.push(fb);
            }
          }
        }
      }
    }
  } catch {}

  return combined;
}

/**
 * Delete a stored feedback record by ID or matching text
 */
export function deleteStoredFeedback(idOrText: string): UserFeedback[] {
  try {
    const current = getStoredFeedback();
    const norm = idOrText.trim().toLowerCase();
    const normClean = normalizeTextForMatch(idOrText);
    const updated = current.filter(
      (f) =>
        f.id !== idOrText &&
        (f.fullText || "").trim().toLowerCase() !== norm &&
        (f.textSnippet || "").trim().toLowerCase() !== norm &&
        normalizeTextForMatch(f.fullText || "") !== normClean &&
        normalizeTextForMatch(f.textSnippet || "") !== normClean.slice(0, 100)
    );
    safeLocalStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(updated));
    triggerDetectionCacheClear();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("catchai_feedback_updated"));
    }
    return updated;
  } catch {
    return [];
  }
}

/**
 * Synchronize a user feedback / correction record to the server SQLite database
 * so it appears immediately in the Admin Feedback Loop queue.
 */
export async function syncFeedbackToBackend(feedback: UserFeedback): Promise<void> {
  try {
    const text = (feedback.fullText || feedback.textSnippet || "").trim();
    if (text.length < 10) return;

    const resolved = resolveFeedbackGroundTruth(feedback);
    const correctedLabel = resolved === "ai" ? "ai" : "human";

    await fetch("/api/feedback/correction", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        submission_text: text,
        corrected_label: correctedLabel,
        model_prediction: typeof feedback.confidenceScore === "number" ? feedback.confidenceScore / 100 : undefined,
        model_verdict: feedback.prediction,
        original_features: feedback.features,
        is_correct: feedback.isCorrect
      })
    });
  } catch (err) {
    console.warn("Failed to sync feedback to backend:", err);
  }
}

/**
 * Save or update a feedback record in local storage and sync to backend
 */
export function saveFeedback(feedback: UserFeedback): UserFeedback[] {
  try {
    const current = getStoredFeedback();
    const normTarget = normalizeTextForMatch(feedback.fullText || feedback.textSnippet || "");
    const existingIndex = current.findIndex(
      (f) =>
        f.id === feedback.id ||
        (f.textSnippet && feedback.textSnippet && f.textSnippet === feedback.textSnippet) ||
        (normTarget.length > 20 && normalizeTextForMatch(f.fullText || f.textSnippet || "") === normTarget)
    );

    let updated: UserFeedback[];
    if (existingIndex >= 0) {
      updated = [...current];
      updated[existingIndex] = { ...updated[existingIndex], ...feedback };
    } else {
      updated = [feedback, ...current];
    }

    safeLocalStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(updated));
    triggerDetectionCacheClear();
    syncFeedbackToBackend(feedback).catch(() => {});

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("catchai_feedback_updated"));
    }
    return updated;
  } catch (err) {
    console.error("Failed to save user feedback:", err);
    return [];
  }
}

/**
 * Find existing feedback for a specific text or id
 */
export function findFeedbackForText(text: string): UserFeedback | null {
  if (!text || text.trim().length < 10) return null;
  const all = getAllFlaggedAndVerifiedSamples();
  const cleanSnippet = text.slice(0, 120).trim();
  const normInput = normalizeTextForMatch(text);
  const normSnippet = normalizeTextForMatch(cleanSnippet);

  for (const f of all) {
    if (f.fullText === text || f.textSnippet === cleanSnippet) {
      return f;
    }
    const fNormFull = normalizeTextForMatch(f.fullText || "");
    const fNormSnip = normalizeTextForMatch(f.textSnippet || "");
    if (fNormFull && fNormFull === normInput) {
      return f;
    }
    if (fNormSnip && fNormSnip.length >= 30 && normSnippet.startsWith(fNormSnip.slice(0, 60))) {
      return f;
    }
  }
  return null;
}

export interface FeedbackCalibrationAdjustment {
  shift: number; // e.g. -35 for Human, +35 for AI
  matchType: "exact_feedback" | "similarity_memory" | null;
  label?: "human" | "ai";
  similarity?: number;
  feedbackRecord?: UserFeedback;
}

/**
 * Computes the verified feedback calibration shift for a given text and 7-axis Z-score vector:
 * 1. Exact or near-exact text match -> shifts AI score by -35% (Human) or +35% (AI)
 * 2. High stylometric Z-vector cosine similarity (>= 0.88) -> shifts AI score proportionally (up to ±22%)
 */
export function computeFeedbackCalibrationShift(
  text: string,
  targetZVector?: number[]
): FeedbackCalibrationAdjustment {
  if (!text || text.trim().length < 15) {
    return { shift: 0, matchType: null };
  }

  try {
    // 1. Check exact / near-exact match first
    const exactMatch = findFeedbackForText(text);
    if (exactMatch) {
      const gt = resolveFeedbackGroundTruth(exactMatch);
      if (gt === "human") {
        return {
          shift: -35,
          matchType: "exact_feedback",
          label: "human",
          similarity: 1.0,
          feedbackRecord: exactMatch
        };
      }
      if (gt === "ai") {
        return {
          shift: 35,
          matchType: "exact_feedback",
          label: "ai",
          similarity: 1.0,
          feedbackRecord: exactMatch
        };
      }
    }

    // 2. Check near-exact token Jaccard similarity or 7-axis stylometric feature similarity
    const allSamples = getAllFlaggedAndVerifiedSamples();
    if (allSamples.length === 0) {
      return { shift: 0, matchType: null };
    }

    const inputWords = new Set(
      normalizeTextForMatch(text)
        .split(" ")
        .filter((w) => w.length > 2)
    );

    const zVec = targetZVector || computeZScoresForText(text);
    const zNorm = Math.sqrt(zVec.reduce((acc, v) => acc + v * v, 0)) || 1e-6;

    let bestSim = -1;
    let bestSample: UserFeedback | null = null;
    let bestLabel: "human" | "ai" | null = null;

    for (const sample of allSamples) {
      const label = resolveFeedbackGroundTruth(sample);
      if (!label) continue;

      const sampleText = (sample.fullText || sample.textSnippet || "").trim();
      if (sampleText.length < 15) continue;

      // Check high lexical overlap (e.g. slightly edited version of the flagged text)
      if (inputWords.size >= 8) {
        const sampleWords = new Set(
          normalizeTextForMatch(sampleText)
            .split(" ")
            .filter((w) => w.length > 2)
        );
        if (sampleWords.size >= 8) {
          let intersection = 0;
          inputWords.forEach((w) => {
            if (sampleWords.has(w)) intersection++;
          });
          const union = inputWords.size + sampleWords.size - intersection;
          const jaccard = union > 0 ? intersection / union : 0;
          if (jaccard >= 0.82) {
            return {
              shift: label === "human" ? -35 : 35,
              matchType: "exact_feedback",
              label,
              similarity: parseFloat(jaccard.toFixed(3)),
              feedbackRecord: sample
            };
          }
        }
      }

      // Compute 7-dimensional stylometric Z-vector cosine similarity
      let sampleZ: number[];
      if (sample.features) {
        const fz = computeFeatureZScores(sample.features);
        sampleZ = [fz.burstinessZ, fz.aslZ, fz.lexZ, fz.repZ, fz.entZ, fz.puncZ, fz.wordZ];
      } else {
        sampleZ = computeZScoresForText(sampleText);
      }

      const sNorm = Math.sqrt(sampleZ.reduce((acc, v) => acc + v * v, 0)) || 1e-6;
      let dot = 0;
      let sqDist = 0;
      for (let i = 0; i < 7; i++) {
        dot += (zVec[i] || 0) * (sampleZ[i] || 0);
        const diff = (zVec[i] || 0) - (sampleZ[i] || 0);
        sqDist += diff * diff;
      }
      const cosineSim = dot / (zNorm * sNorm);
      const euclidDist = Math.sqrt(sqDist / 7);

      // Require both strong directional alignment (cosine >= 0.88) and close magnitude (euclidDist <= 0.75)
      if (cosineSim >= 0.88 && euclidDist <= 0.75 && cosineSim > bestSim) {
        bestSim = cosineSim;
        bestSample = sample;
        bestLabel = label;
      }
    }

    if (bestSample && bestLabel && bestSim >= 0.88) {
      // Proportional similarity boost: scales smoothly from 14% at 0.88 similarity up to 22% at 1.0
      const magnitude = Math.round(14 + ((bestSim - 0.88) / 0.12) * 8);
      const shift = bestLabel === "human" ? -magnitude : magnitude;
      return {
        shift,
        matchType: "similarity_memory",
        label: bestLabel,
        similarity: parseFloat(bestSim.toFixed(3)),
        feedbackRecord: bestSample
      };
    }
  } catch (err) {
    console.warn("Feedback calibration lookup warning:", err);
  }

  return { shift: 0, matchType: null };
}

/**
 * Create a new structured UserFeedback item
 */
export function createFeedbackRecord(
  result: DetectionResult,
  text: string,
  isCorrect: boolean,
  actualLabel?: GroundTruthLabel,
  userNotes?: string,
  category?: string
): UserFeedback {
  const sanitizedText = sanitizeUserInput(text);
  const snippet = sanitizedText.slice(0, 120).trim();

  // If actualLabel is not explicitly specified, derive a default based on agreement
  let resolvedLabel: GroundTruthLabel = actualLabel || "uncertain";
  if (!actualLabel) {
    if (isCorrect) {
      if (result.overall_prediction === "Likely AI-Generated") resolvedLabel = "ai";
      else if (result.overall_prediction === "Likely Human-Written") resolvedLabel = "human";
    } else {
      // If user marked incorrect, invert default assumption (borderline defaults to human when flagged wrong)
      if (result.overall_prediction === "Likely Human-Written") resolvedLabel = "ai";
      else resolvedLabel = "human";
    }
  }

  return {
    id: `fb-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp: new Date().toISOString(),
    isCorrect,
    actualLabel: resolvedLabel,
    userNotes: userNotes ? sanitizeUserInput(userNotes) : undefined,
    category: category || "General",
    prediction: result.overall_prediction,
    confidenceScore: result.confidence_score,
    wordCount: result.features?.totalWords || countWords(text),
    textSnippet: snippet,
    fullText: sanitizedText,
    features: result.features
  };
}



